export type TennisPoint = '0' | '15' | '30' | '40' | 'AD';

export interface TennisRallyState {
  pointA: TennisPoint;
  pointB: TennisPoint;
  tiebreakPointsA: number;
  tiebreakPointsB: number;
  isTiebreak: boolean;
}

export interface TennisSetScore {
  setNumber: number;
  gamesA: number;
  gamesB: number;
  isComplete: boolean;
}

export const INITIAL_RALLY_STATE: TennisRallyState = {
  pointA: '0',
  pointB: '0',
  tiebreakPointsA: 0,
  tiebreakPointsB: 0,
  isTiebreak: false,
};

const POINT_SEQUENCE: Record<TennisPoint, TennisPoint | 'game'> = {
  '0': '15',
  '15': '30',
  '30': '40',
  '40': 'game',
  'AD': 'game',
};

export const MAX_TENNIS_GAMES = 7;
export const MAX_DEUCE_GAMES = 20;

/**
 * Memeriksa apakah satu set tenis telah selesai sesuai regulasi:
 * - Deuce OFF:
 *   - 6 games dengan selisih >= 2 (contoh 6-0 s/d 6-4)
 *   - 7-5
 *   - 7-6 (kalau 6 sama, yang sampai 7 pertama menang)
 * - Deuce ON (Advantage set / selisih 2 game):
 *   - 6 games dengan selisih >= 2 (6-0 s/d 6-4)
 *   - 7-5
 *   - Pada 6-6 ke atas: harus selisih >= 2 (contoh 8-6, 9-7, 10-8, dst.)
 *   (Skor 7-6 belum selesai karena baru selisih 1)
 */
export function isTennisSetComplete(
  gamesA: number,
  gamesB: number,
  deuceEnabled: boolean = false
): boolean {
  if (gamesA === gamesB) return false;
  const max = Math.max(gamesA, gamesB);
  const min = Math.min(gamesA, gamesB);

  if (!deuceEnabled) {
    if (max === 6 && max - min >= 2) return true;
    if (max === 7 && (min === 5 || min === 6)) return true;
    return false;
  }

  // Deuce ON
  if (max === 6 && max - min >= 2) return true;
  if (max === 7 && min === 5) return true;
  if (max >= 8 && min >= 6 && max - min >= 2) return true;
  return false;
}

/**
 * Memeriksa apakah pasangan skor set (A, B) adalah kondisi yang valid dalam tenis,
 * baik saat sedang berlangsung maupun saat sudah selesai.
 */
export function isValidTennisGameScore(
  gamesA: number,
  gamesB: number,
  deuceEnabled: boolean = false
): boolean {
  if (gamesA < 0 || gamesB < 0) return false;

  if (!deuceEnabled) {
    if (gamesA > MAX_TENNIS_GAMES || gamesB > MAX_TENNIS_GAMES) {
      return false;
    }

    // Selesai: 6-0 s/d 6-4 atau 0-6 s/d 4-6
    if ((gamesA === 6 && gamesB <= 4) || (gamesB === 6 && gamesA <= 4)) return true;

    // Selesai: 7-5 atau 5-7
    if ((gamesA === 7 && gamesB === 5) || (gamesB === 7 && gamesA === 5)) return true;

    // Selesai: 7-6 atau 6-7 (tiebreak / sudden death)
    if ((gamesA === 7 && gamesB === 6) || (gamesB === 7 && gamesA === 6)) return true;

    // Sedang berlangsung: kedua tim <= 5
    if (gamesA <= 5 && gamesB <= 5) return true;

    // Sedang berlangsung: 6-5 atau 5-6
    if ((gamesA === 6 && gamesB === 5) || (gamesB === 6 && gamesA === 5)) return true;

    // Sedang berlangsung: 6-6 (sebelum/saat tiebreak)
    if (gamesA === 6 && gamesB === 6) return true;

    return false;
  }

  // Deuce ON:
  if (gamesA > MAX_DEUCE_GAMES || gamesB > MAX_DEUCE_GAMES) return false;

  // Selesai normal (<= 7)
  if ((gamesA === 6 && gamesB <= 4) || (gamesB === 6 && gamesA <= 4)) return true;
  if ((gamesA === 7 && gamesB === 5) || (gamesB === 7 && gamesA === 5)) return true;

  // Sedang berlangsung: kedua tim <= 5
  if (gamesA <= 5 && gamesB <= 5) return true;

  // Sedang berlangsung: 6-5 atau 5-6
  if ((gamesA === 6 && gamesB === 5) || (gamesB === 6 && gamesA === 5)) return true;

  // Sedang berlangsung pada 6-6 ke atas:
  const max = Math.max(gamesA, gamesB);
  const min = Math.min(gamesA, gamesB);

  if (min >= 6) {
    if (max - min <= 1) return true; // 6-6, 7-6, 7-7, 8-7, 8-8 ...
    if (max - min === 2) return true; // 8-6, 9-7, 10-8 ...
  }

  return false;
}

/**
 * Validasi apakah skor pada satu sisi boleh ditambah (bump +) atau dikurangi (bump -):
 * - Pengurangan selalu diizinkan selama >= 0 (untuk koreksi).
 * - Penambahan dilarang jika set sudah selesai, atau jika melebihi batas tenis.
 */
export function canBumpTennisScore(
  currentA: number,
  currentB: number,
  side: 'a' | 'b',
  delta: number,
  deuceEnabled: boolean = false
): boolean {
  if (delta === 0) return true;
  const nextA = side === 'a' ? currentA + delta : currentA;
  const nextB = side === 'b' ? currentB + delta : currentB;
  if (nextA < 0 || nextB < 0) return false;

  // Mengurangi skor untuk koreksi selalu diizinkan
  if (delta < 0) return true;

  // Jika set sudah selesai, tidak boleh ditambah lagi
  if (isTennisSetComplete(currentA, currentB, deuceEnabled)) return false;

  return isValidTennisGameScore(nextA, nextB, deuceEnabled);
}

export function getTennisSetWinner(gamesA: number, gamesB: number): 'a' | 'b' | '' {
  if (gamesA === gamesB || (gamesA === 0 && gamesB === 0)) return '';
  return gamesA > gamesB ? 'a' : 'b';
}

/**
 * Menghitung kemenangan tiebreak (pertama mencapai 7 poin dengan selisih min. 2).
 */
export function isTiebreakComplete(tbA: number, tbB: number): boolean {
  if (tbA === tbB) return false;
  const max = Math.max(tbA, tbB);
  const min = Math.min(tbA, tbB);
  return max >= 7 && max - min >= 2;
}

export function getTiebreakWinner(tbA: number, tbB: number): 'a' | 'b' | '' {
  if (!isTiebreakComplete(tbA, tbB)) return '';
  return tbA > tbB ? 'a' : 'b';
}

/**
 * Menghitung transisi poin rally berikutnya:
 * - Normal: 0 -> 15 -> 30 -> 40 -> Game
 * - Deuce ON:
 *   - Saat 40-40, poin berikutnya menjadi AD (Advantage) bagi pencetak poin.
 *   - Jika pemegang AD mencetak poin lagi -> Game!
 *   - Jika lawan mencetak poin saat posisi AD -> kembali ke 40-40 (Deuce).
 * - Deuce OFF (Punto de Oro / Golden Point):
 *   - Saat 40-40, pencetak poin langsung memenangkan Game.
 */
export function nextTennisRallyPoint(
  current: TennisRallyState,
  scoringSide: 'a' | 'b',
  deuceEnabled: boolean = false
): {
  nextRally: TennisRallyState;
  gameWon: boolean;
  winnerSide?: 'a' | 'b';
} {
  // Jika dalam mode Tiebreak (terjadi pada 6-6)
  if (current.isTiebreak) {
    const nextTbA = scoringSide === 'a' ? current.tiebreakPointsA + 1 : current.tiebreakPointsA;
    const nextTbB = scoringSide === 'b' ? current.tiebreakPointsB + 1 : current.tiebreakPointsB;

    if (isTiebreakComplete(nextTbA, nextTbB)) {
      return {
        nextRally: { ...INITIAL_RALLY_STATE },
        gameWon: true,
        winnerSide: scoringSide,
      };
    }

    return {
      nextRally: {
        ...current,
        tiebreakPointsA: nextTbA,
        tiebreakPointsB: nextTbB,
      },
      gameWon: false,
    };
  }

  const myPoint = scoringSide === 'a' ? current.pointA : current.pointB;
  const oppPoint = scoringSide === 'a' ? current.pointB : current.pointA;

  // 1. Jika scoring side sudah memegang AD -> Menang Game!
  if (myPoint === 'AD') {
    return {
      nextRally: { ...INITIAL_RALLY_STATE },
      gameWon: true,
      winnerSide: scoringSide,
    };
  }

  // 2. Jika lawan memegang AD dan scoring side mencetak poin -> kembali ke Deuce (40-40)
  if (oppPoint === 'AD') {
    return {
      nextRally: {
        ...current,
        pointA: '40',
        pointB: '40',
      },
      gameWon: false,
    };
  }

  // 3. Saat 40-40 (Deuce vs Punto de Oro)
  if (myPoint === '40' && oppPoint === '40') {
    if (deuceEnabled) {
      // Deuce Mode Aktif: Masuk posisi Advantage (AD)
      return {
        nextRally: {
          ...current,
          pointA: scoringSide === 'a' ? 'AD' : '40',
          pointB: scoringSide === 'b' ? 'AD' : '40',
        },
        gameWon: false,
      };
    } else {
      // Punto de Oro / Golden Point: poin penentu langsung Game
      return {
        nextRally: { ...INITIAL_RALLY_STATE },
        gameWon: true,
        winnerSide: scoringSide,
      };
    }
  }

  // 4. Jika sudah 40 dan lawan di bawah 40 (0, 15, 30) -> Game!
  if (myPoint === '40') {
    return {
      nextRally: { ...INITIAL_RALLY_STATE },
      gameWon: true,
      winnerSide: scoringSide,
    };
  }

  // 5. Kemajuan poin normal: 0 -> 15 -> 30 -> 40
  const nextPoint = POINT_SEQUENCE[myPoint];
  if (nextPoint === 'game') {
    return {
      nextRally: { ...INITIAL_RALLY_STATE },
      gameWon: true,
      winnerSide: scoringSide,
    };
  }

  return {
    nextRally: {
      ...current,
      pointA: scoringSide === 'a' ? nextPoint : current.pointA,
      pointB: scoringSide === 'b' ? nextPoint : current.pointB,
    },
    gameWon: false,
  };
}

/**
 * Menambahkan 1 game ke set yang sedang berlangsung,
 * dan otomatis mengaktifkan mode tiebreak jika skor mencapai 6-6.
 */
export function recordGameWinToSet(
  gamesA: number,
  gamesB: number,
  winnerSide: 'a' | 'b',
  deuceEnabled: boolean = false
): {
  nextGamesA: number;
  nextGamesB: number;
  isSetComplete: boolean;
  isTiebreakNext: boolean;
} {
  const nextA = winnerSide === 'a' ? gamesA + 1 : gamesA;
  const nextB = winnerSide === 'b' ? gamesB + 1 : gamesB;
  const setComplete = isTennisSetComplete(nextA, nextB, deuceEnabled);
  const tiebreakNext = !deuceEnabled && !setComplete && nextA === 6 && nextB === 6;

  return {
    nextGamesA: nextA,
    nextGamesB: nextB,
    isSetComplete: setComplete,
    isTiebreakNext: tiebreakNext,
  };
}

/**
 * Menghitung kemenangan match (Best of 1 vs Best of 3):
 * - Best of 1: butuh 1 set menang
 * - Best of 3: butuh 2 set menang
 */
export function getMatchWinner(
  sets: { gamesA: number; gamesB: number }[],
  maxSets: number
): 'a' | 'b' | '' {
  const neededWins = maxSets <= 1 ? 1 : 2;
  let winsA = 0;
  let winsB = 0;

  for (const s of sets) {
    if (s.gamesA === s.gamesB || (s.gamesA === 0 && s.gamesB === 0)) continue;
    if (s.gamesA > s.gamesB) winsA++;
    else winsB++;
  }

  if (winsA >= neededWins) return 'a';
  if (winsB >= neededWins) return 'b';
  if (winsA > winsB) return 'a';
  if (winsB > winsA) return 'b';
  return '';
}

/**
 * Format string representasi poin rally saat ini.
 */
export function formatRallyScore(state: TennisRallyState): {
  scoreA: string;
  scoreB: string;
  label?: string;
} {
  if (state.isTiebreak) {
    return {
      scoreA: String(state.tiebreakPointsA),
      scoreB: String(state.tiebreakPointsB),
      label: 'TIEBREAK',
    };
  }

  if (state.pointA === 'AD') {
    return {
      scoreA: 'AD',
      scoreB: '40',
      label: 'ADVANTAGE TIM A',
    };
  }

  if (state.pointB === 'AD') {
    return {
      scoreA: '40',
      scoreB: 'AD',
      label: 'ADVANTAGE TIM B',
    };
  }

  if (state.pointA === '40' && state.pointB === '40') {
    return {
      scoreA: '40',
      scoreB: '40',
      label: 'DEUCE',
    };
  }

  let label: string | undefined;
  if (state.pointA === '40' && state.pointB !== '40') label = 'GAME POINT TIM A';
  else if (state.pointB === '40' && state.pointA !== '40') label = 'GAME POINT TIM B';

  return {
    scoreA: state.pointA,
    scoreB: state.pointB,
    label,
  };
}
